import { settled } from 'common/types/Event-type.type';

export const EVENTS_TO_REFETCH_VIDEOS = [
  settled('CompanyUpdateAdded'),
  settled('CompanyUpdateUpdated'),
  settled('CompanyUpdateDeleted'),
  settled('PostsModified'),
  settled('PostDeleted'),
];

export const getFileNameAndFileExtension = (value: string | undefined) => {
  const texts = value?.split('.') || [];
  const extension = texts.pop();
  const fileName = texts.join('.');

  return { fileName, extension };
};

export const getOptimizedImageUrl = (
  src: string | null | undefined,
  width?: number,
  height?: number,
) => {
  let srcURL;
  if (src && width) {
    console.assert(width > 0, 'Optimized image width must be positive');
    try {
      srcURL = new URL(src ?? '');
    } catch {
      srcURL = new URL('http://localhost:3000');
    }
    const srcURLSearchParams = srcURL.searchParams;
    srcURLSearchParams.append('w', `${bucketSize(width)}`);
    if (height) {
      console.assert(height > 0, 'Optimized image height must be positive');
      srcURLSearchParams.append('h', `${bucketSize(height)}`);
    }
    srcURLSearchParams.append('dpr', `${bucketDpr(window.devicePixelRatio ?? 1)}`);
  }
  return srcURL ? srcURL.toString() : srcURL;
};

/**
 * Bucket the browser's devicePixelRatio upward to the nearest tier.
 *
 * Always rounds up so the server produces a slightly larger image than
 * needed -- the browser downscales it, which looks sharp. Rounding down
 * would cause blurry upscaling on the client.
 *
 * Bucketing reduces the number of distinct image variants that need to
 * be transformed and cached server-side. See image-variants.md for the
 * production traffic analysis that informed these tiers.
 */
function bucketDpr(dpr: number): number {
  if (dpr <= 1) return 1;
  if (dpr <= 1.5) return 1.5;
  if (dpr <= 2) return 2;
  if (dpr <= 3) return 3;
  return 4;
}

/**
 * Bucket a pixel dimension (width or height) upward to the nearest tier.
 *
 * Production traffic shows ~10 logical base sizes. Bucketing reduces
 * variant count without visible quality loss -- at small sizes the
 * difference between e.g. 18px and 20px is imperceptible, and the
 * server-side transform cache benefits from fewer unique keys.
 */
function bucketSize(px: number): number {
  const tiers = [32, 64, 128, 256, 550, 1024];
  for (const tier of tiers) {
    if (px <= tier) return tier;
  }
  return px;
}
